import { createSlice } from "@reduxjs/toolkit";
import {
  fetchUserRoles,
  fetchUsers,
  saveUser,
  deleteUser,
  updateUser,
  createUserRole,
  deleteUserRole,
  updateUserRole,
  fetchUserSubscription,
  updateUserSubscription,
  inviteUser,
  fetchInvitations,
} from "./users.actions";
import { EMPTY_STRING, DEFAULT_LIMIT, ZERO, ONE } from "../../constants";
import { deleteApplication, createApplication } from "../Applications";

const paginationFilters = {
  name: EMPTY_STRING,
  page: ONE,
  limit: DEFAULT_LIMIT,
};

const invitationPaginationFilters = {
  userName: EMPTY_STRING,
  page: ONE,
  limit: DEFAULT_LIMIT,
  organizationId: EMPTY_STRING,
};

const initialState = {
  users: {
    data: [],
  },
  userRoles: [],
  selectedUser: null,
  selectedUserRole: null,
  totalUsers: ZERO,
  usersFilter: paginationFilters,
  userSubscription: null,
  invitations: [],
  inviteUser: null,
  invitationFilters: invitationPaginationFilters,
  userInvited: false,
};

const slice = createSlice({
  name: "users",
  initialState,
  reducers: {
    setSelectedUserRole(state, action) {
      state.selectedUserRole = action.payload;
    },
    setSelectedUser(state, action) {
      state.selectedUser = action.payload;
    },
    addUser(state, action) {
      state.users = state.users + action.payload;
    },
    setUsersFilters(state, action) {
      state.usersFilter = action.payload || paginationFilters;
    },
    setInvitationFilters(state, action) {
      state.invitationFilters = action.payload || invitationPaginationFilters;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.users = action.payload || state.users;
        state.totalUsers = action.payload.total;
      })
      .addCase(fetchUserRoles.fulfilled, (state, action) => {
        state.userRoles = action.payload || state.userRoles;
      })
      .addCase(saveUser.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.users =
          {
            ...state.users,
            total: state.users.total + ONE,
            data: [...state.users.data, action.payload],
          } || state.users;
        state.totalUsers = state.totalUsers + ONE;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        if (!action.payload) return;
        const updatedUsers = state.users.data.filter(
          (item) => item.id !== action.payload.id
        );
        state.users = {
          ...state.users,
          total: state.users.total - ONE,
          data: updatedUsers,
        };
        state.totalUsers = state.totalUsers - ONE;
        const targetPage =
          state.totalUsers % DEFAULT_LIMIT === ZERO
            ? state.usersFilter.page - ONE
            : state.usersFilter.page;
        state.usersFilter = { ...state.usersFilter, page: targetPage };
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        if (!action.payload) return;
        const updatedUsers = state.users.data.map((user) =>
          user.id === action.payload.id ? { ...user, ...action.payload } : user
        );
        state.users = { ...state.users, data: updatedUsers };
        state.selectedUser = null;
      })
      .addCase(createUserRole.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.userRoles = [...state.userRoles, action.payload];
      })
      .addCase(deleteUserRole.fulfilled, (state, action) => {
        if (!action.payload) return;
        const updatedUsersRoles = state.userRoles.filter(
          (item) => item.id !== action.payload.id
        );
        state.userRoles = updatedUsersRoles;
      })
      .addCase(updateUserRole.fulfilled, (state, action) => {
        if (!action.payload) return;
        const updatedUserRoles = state.userRoles.map((user) =>
          user.id === action.payload.id ? { ...user, ...action.payload } : user
        );
        state.userRoles = updatedUserRoles;
        state.selectedUserRole = null;
      })
      .addCase(createApplication.fulfilled, (state, action) => {
        if (!action.payload) return;
        const { id, createdAt, createdBy, updatedAt, organizationId } =
          action.payload;
        const formattedUserApplicationAssociations = {
          id: EMPTY_STRING,
          appId: id,
          userId: createdBy,
          createdAt,
          updatedAt,
          organizationId,
        };
        const updatedUsers = state.users.data.map((user) =>
          user.id === createdBy
            ? {
                ...user,
                userApplicationAssociations: [
                  ...user.userApplicationAssociations,
                  formattedUserApplicationAssociations,
                ],
              }
            : user
        );
        state.users = { ...state.users, data: updatedUsers };
      })
      .addCase(deleteApplication.fulfilled, (state, action) => {
        if (!action.payload) return;
        const { id, createdBy } = action.payload;
        const updatedUsers = state.users.data.map((user) =>
          user.id === createdBy
            ? {
                ...user,
                userApplicationAssociations: [
                  ...user.userApplicationAssociations.filter(
                    (each) => each.appId !== id
                  ),
                ],
              }
            : user
        );
        state.users = { ...state.users, data: updatedUsers };
      })

      .addCase(fetchUserSubscription.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.userSubscription = action.payload;
      })

      .addCase(updateUserSubscription.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.userSubscription = action.payload;
      })
      .addCase(inviteUser.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.inviteUser = action.payload;
      })
      .addCase(fetchInvitations.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.invitations = action.payload;
        state.userInvited = true;
      });
  },
});

export default slice;

export const { name, actions, reducer } = slice;
